// How a template's approval status is shown, on a card and on its own page.

export const STATUS = {
  pending: { label: "Waiting for Meta", pill: "bg-[#FEF3C7] text-[#92400E]", dot: "bg-[#D97706]" },
  approved: { label: "Approved", pill: "bg-[#DCFCE7] text-[#166534]", dot: "bg-[#16A34A]" },
  rejected: { label: "Rejected", pill: "bg-[#FEE2E2] text-[#991B1B]", dot: "bg-[#DC2626]" },
  failed: { label: "Not submitted", pill: "bg-[#FEE2E2] text-[#991B1B]", dot: "bg-[#DC2626]" },
  unknown: { label: "Unknown", pill: "bg-[#F3F4F6] text-[#374151]", dot: "bg-[#9CA3AF]" },
};

export function StatusPill({ status, className = "" }) {
  const s = STATUS[status] || STATUS.unknown;
  return (
    <span className={`inline-flex h-7 shrink-0 items-center gap-[7px] rounded-full px-3 text-[12.5px] font-semibold ${s.pill} ${className}`}>
      <span className={`h-[7px] w-[7px] rounded-full ${s.dot} ${status === "pending" ? "sa-pulse-dot" : ""}`} aria-hidden="true" />
      {s.label}
    </span>
  );
}

// Where the template is on its way to a decision: sent, with Meta, decided.
export function Journey({ status }) {
  const failed = status === "failed";
  const decided = status === "approved" || status === "rejected";
  const steps = [
    { label: failed ? "Not sent" : "Sent to Chat360", state: failed ? "bad" : "done" },
    { label: "Meta reviews it", state: failed ? "todo" : decided ? "done" : "active" },
    { label: status === "rejected" ? "Rejected" : status === "approved" ? "Approved" : "Decision", state: status === "approved" ? "done" : status === "rejected" ? "bad" : "todo" },
  ];
  const tone = { done: "bg-[#16A34A]", active: "bg-[#D97706] sa-pulse-dot", bad: "bg-[#DC2626]", todo: "bg-[#D1D5DB]" };
  return (
    <ol className="m-0 flex list-none flex-wrap items-center gap-2 p-0" aria-label="Progress">
      {steps.map((s, i) => (
        <li key={i} className="flex items-center gap-2">
          <span className={`h-2 w-2 rounded-full ${tone[s.state]}`} aria-hidden="true" />
          <span className={`text-[13.5px] ${s.state === "todo" ? "text-[#9CA3AF]" : "text-[#374151]"} ${s.state === "active" ? "font-semibold" : ""}`}>{s.label}</span>
          {i < steps.length - 1 && <span className="mx-1 h-px w-6 bg-[#D1D5DB]" aria-hidden="true" />}
        </li>
      ))}
    </ol>
  );
}

// The round badge in front of a template's name: its first two initials, in a colour that
// tells the category (blue for utility, violet for marketing).
const AVATAR = {
  utility: "bg-[#E0E7FF] text-[#3730A3]",
  marketing: "bg-[#F3E8FF] text-[#6B21A8]",
};

export const initials = (name) => {
  const words = String(name || "").split(/[^a-z0-9]+/i).filter(Boolean);
  return ((words[0]?.[0] || "") + (words[1]?.[0] || words[0]?.[1] || "")).toUpperCase();
};

export function TemplateAvatar({ name, category }) {
  const tone = AVATAR[String(category || "").toLowerCase()] || AVATAR.utility;
  return (
    <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-[14px] font-semibold tracking-[0.02em] ${tone}`} aria-hidden="true">
      {initials(name)}
    </span>
  );
}
